export const PATIENT_AGE_HINT =
  'Use two-digit months .01–.12 after the dot (e.g. 2.08 = 2 years 8 months). .12 rolls up to the next year.';

/** Years only, or years with exactly two month digits (.01–.12). */
export const PATIENT_AGE_PATTERN = /^\d+(\.(0[1-9]|1[0-2]))?$/;

function parsePatientAgeParts(value: string | number): { years: number; monthStr: string | null } {
  const raw = String(value).trim();
  if (!raw.includes('.')) {
    return { years: Number.parseInt(raw, 10), monthStr: null };
  }

  const [yearsStr, monthStr] = raw.split('.');
  return {
    years: Number.parseInt(yearsStr, 10),
    monthStr,
  };
}

/** Convert 12 months to the next full year (e.g. 2.12 → 3). */
export function normalizePatientAgeForStorage(
  value: string | number | null | undefined,
): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const raw = String(value).trim();
  if (raw.endsWith('.') || !PATIENT_AGE_PATTERN.test(raw)) {
    return null;
  }

  const { years, monthStr } = parsePatientAgeParts(raw);
  if (monthStr === null) {
    return years;
  }

  const months = Number.parseInt(monthStr, 10);
  if (months === 12) {
    return years + 1;
  }

  return Number.parseFloat(`${years}.${monthStr}`);
}

export function getPatientAgeError(
  value: string | number | null | undefined,
): string | null {
  if (value === null || value === undefined || value === '') {
    return 'Age is required.';
  }

  const raw = String(value).trim();
  if (raw.endsWith('.')) {
    return 'Enter months as two digits (01–12).';
  }

  if (raw.includes('.')) {
    const monthPart = raw.split('.')[1] ?? '';
    if (monthPart.length === 1) {
      return 'Use two-digit months (e.g. 2.08 for 8 months, not 2.8).';
    }
    if (monthPart.length > 2) {
      return 'Months must be two digits from 01 to 12.';
    }
  }

  if (!PATIENT_AGE_PATTERN.test(raw)) {
    return 'Use years with optional months .01–.12 (e.g. 2.08 or 2.10).';
  }

  const years = Number.parseInt(raw.split('.')[0], 10);
  if (years < 0 || years > 150) {
    return 'Years must be between 0 and 150.';
  }

  const normalized = normalizePatientAgeForStorage(value);
  if (normalized !== null && normalized > 150) {
    return 'Age cannot exceed 150 years.';
  }

  return null;
}

export function isValidPatientAge(
  value: string | number | null | undefined,
): boolean {
  return getPatientAgeError(value) === null;
}

/** Restrict characters and month length while typing. */
export function normalizePatientAgeInput(
  value: string | number | null | undefined,
): string | number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  let raw = String(value).replace(/[^\d.]/g, '');
  const firstDot = raw.indexOf('.');
  if (firstDot === -1) {
    const yearsOnly = raw.replace(/^0+(?=\d)/, '');
    return yearsOnly === '' ? null : Number.parseInt(yearsOnly, 10);
  }

  const years = raw.slice(0, firstDot) || '0';
  let months = raw.slice(firstDot + 1).replace(/\./g, '').slice(0, 2);

  if (months.length === 2) {
    const monthNum = Number.parseInt(months, 10);
    if (monthNum > 12) {
      months = '12';
    } else if (monthNum === 0) {
      months = '01';
    }
  }

  if (raw.endsWith('.') && months.length === 0) {
    return `${years}.`;
  }

  if (!months.length) {
    return Number.parseInt(years, 10);
  }

  return `${years}.${months}`;
}

/** Pad single-digit months and roll up .12 on blur or save. */
export function finalizePatientAgeInput(
  value: string | number | null | undefined,
): string | number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const raw = String(value).trim();
  if (!raw.includes('.')) {
    return Number.parseInt(raw, 10);
  }

  const [years, monthPart] = raw.split('.');
  if (!monthPart) {
    return `${years}.`;
  }

  const months = monthPart.length === 1 ? `0${monthPart}` : monthPart.slice(0, 2);
  const combined = `${years}.${months}`;
  const stored = normalizePatientAgeForStorage(combined);

  return stored ?? combined;
}

/** Show legacy/saved values in two-digit month format. */
export function formatPatientAgeForDisplay(
  value: string | number | null | undefined,
): string | number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const raw = String(value).trim();
  if (!raw.includes('.')) {
    return Number.parseInt(raw, 10);
  }

  const [years, monthPart] = raw.split('.');
  const months = Number.parseInt(monthPart, 10);
  if (Number.isNaN(months) || months <= 0) {
    return Number.parseInt(years, 10);
  }

  const monthStr = String(months).padStart(2, '0');
  if (months === 12) {
    return Number.parseInt(years, 10) + 1;
  }

  return `${years}.${monthStr}`;
}

/** Display age without trailing zero months (e.g. 24.00 → 24, 2.08 → 2.08). */
export function formatPatientAgeDisplay(
  value: string | number | null | undefined,
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const raw = String(value).trim();
  if (!raw.includes('.')) {
    return raw;
  }

  const [years, monthPart] = raw.split('.');
  const months = Number.parseInt(monthPart, 10);
  if (!months || Number.isNaN(months)) {
    return years;
  }

  return `${years}.${String(months).padStart(2, '0')}`;
}
